/**
 * Helpers puros para construir y analizar rutas con prefijo de idioma.
 *
 * Estrategia: la URL es la fuente de verdad del idioma. El español no lleva
 * prefijo (`/recetas`); el resto sí (`/en/recetas`, `/fr`, `/pt/productos`).
 * Los segmentos de la ruta siguen siendo los españoles en todos los idiomas.
 * Los prefijos desconocidos (`/de/...`) NO se consideran idioma.
 */

import { DEFAULT_LANGUAGE, PREFIXED_LANGUAGES } from './languages';

/**
 * Separa el primer segmento de un pathname del resto.
 * @param {string} pathname - Ruta sin query ni hash.
 * @returns {{first: string, rest: string}} Primer segmento y resto (con `/` inicial o vacío).
 */
const splitFirstSegment = (pathname) => {
  const match = /^\/([^/]*)(\/.*)?$/.exec(pathname || '');
  return match ? { first: match[1], rest: match[2] || '' } : { first: '', rest: '' };
};

/**
 * Indica si un enlace es una ruta interna absoluta (`/algo`) y no una URL
 * externa, un ancla (`#x`), `mailto:`, ruta relativa o `//host`.
 * @param {string} path
 * @returns {boolean}
 */
const isInternalPath = (path) =>
  typeof path === 'string' && path.startsWith('/') && !path.startsWith('//');

/**
 * Obtiene el idioma indicado por el prefijo de la ruta.
 * @param {string} pathname - Ruta, p. ej. `/en/recetas`.
 * @returns {string} Código de idioma; el de por defecto si no hay prefijo válido.
 */
export const getLanguageFromPath = (pathname) => {
  const { first } = splitFirstSegment(pathname);
  return PREFIXED_LANGUAGES.includes(first) ? first : DEFAULT_LANGUAGE;
};

/**
 * Quita el prefijo de idioma (si es válido) de una ruta.
 * @param {string} pathname - Ruta, p. ej. `/en/recetas` o `/en`.
 * @returns {string} Ruta sin prefijo (`/recetas`, `/`).
 */
export const stripLanguagePrefix = (pathname) => {
  const { first, rest } = splitFirstSegment(pathname);
  if (!PREFIXED_LANGUAGES.includes(first)) return pathname || '/';
  return rest === '' || rest === '/' ? '/' : rest;
};

/**
 * Convierte una ruta interna a la versión del idioma indicado.
 * Es idempotente (reemplaza un prefijo previo), conserva query y hash y deja
 * intactos los enlaces que no son rutas internas absolutas.
 * @param {string} path - Ruta interna, p. ej. `/recetas?x=1#a`.
 * @param {string} lang - Idioma destino.
 * @returns {string} Ruta localizada.
 */
export const localizePath = (path, lang) => {
  if (!isInternalPath(path)) return path;

  const suffixStart = path.search(/[?#]/);
  const pathname = suffixStart === -1 ? path : path.slice(0, suffixStart);
  const suffix = suffixStart === -1 ? '' : path.slice(suffixStart);
  const barePath = stripLanguagePrefix(pathname);

  if (!PREFIXED_LANGUAGES.includes(lang)) return `${barePath}${suffix}`;
  return `/${lang}${barePath === '/' ? '' : barePath}${suffix}`;
};

/**
 * Ruta equivalente a la actual en otro idioma (para el selector de idioma).
 * @param {string} currentPathname - Pathname actual.
 * @param {string} targetLang - Idioma destino.
 * @returns {string} Pathname en el idioma destino.
 */
export const switchLanguagePath = (currentPathname, targetLang) =>
  localizePath(stripLanguagePrefix(currentPathname), targetLang);
